import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { generateRandomSixDiginCode } from '@/services';
import {
  orderAppLeasingMapper,
  orderAppMapper
} from '@/services/mappers/orderAppMapper';
import { tsModalInfoMapper } from '@/services/mappers/tsModalInfoMapper';
import type { TModalTSValues } from '@/types/financing';
import type { TNewOrderStatus } from '@/types/offers';

import type { TNewOrderDto } from '../types/newOrder';
import type {
  ClientFullNameInterface,
  TClientInfoStore,
  TNewOrderStore
} from '../types/store';
import { createAsyncThunk } from './create-async-thunk';
import { getResponsesByRequestId } from './offerStep.slice';

export const createNewOrder = createAsyncThunk(
  'newOrder/createNewOrder',
  async (reqDto: TNewOrderDto, { dispatch }) => {
    const response = await autoCreditApi.createOrder(reqDto);
    // TODO Когда полностью перейдем на новый API, удалить resp (29.05.2026)
    const resp = response.resp || response;
    const requestId = response.id || resp?.inputs?.req_body?.request_id;

    await dispatch(getRequestById({ reqId: requestId }));
    if (reqDto.sendTo === 'banks_leasing' || reqDto.sendTo === 'banks') {
      await dispatch(getResponsesByRequestId({ reqId: requestId }));
    }
    if (reqDto.sendTo === 'banks_leasing' || reqDto.sendTo === 'leasing') {
      await dispatch(
        getAutoCredLeasingResponsesByRequestById({ reqId: requestId })
      );
    }
    localStorage.removeItem(reqDto?.uuid);

    return { requestId };
  }
);

export const changeRequestStatus = createAsyncThunk(
  'request/changeRequestStatus',
  async (data: { reqId: string; newStatus: TNewOrderStatus }) => {
    return autoCreditApi.changeRequestStatus(data);
  }
);

export const getRequestById = createAsyncThunk(
  'newOrder/gerRequestById',
  async (dataObject: { reqId: string; abortSignal?: AbortSignal }) => {
    return await autoCreditApi.getRequestById({
      reqId: dataObject.reqId,
      abortSignal: dataObject.abortSignal
    });
  }
);

export const getAutoCredLeasingResponsesByRequestById = createAsyncThunk(
  'newOrder/getAutoCredLeasingResponsesByRequestById',
  async ({
    reqId,
    abortSignal
  }: {
    reqId: string;
    abortSignal?: AbortSignal;
  }) => {
    return autoCreditApi.getLeasingResponsesByRequestId(reqId, abortSignal);
  }
);

const defaultClientInfo = {
  lastName: '',
  firstName: '',
  middleName: '',
  iin: '',
  telNum: '',
  id: '',
  isClientPhoneNumberVerified: true
};

const defaultTsModalValues: TModalTSValues = {
  bodyNum: '',
  carColor: '',
  engineCapacity: '',
  licensePlate: '',
  paymentDate: '',
  registerCompany: '',
  registerDate: '',
  registerNum: '',
  requestUid: '',
  vin: ''
};

const defaultLoan: TNewOrderStore['loan'] = {
  amount: '',
  auto: ''
};

const initialState: TNewOrderStore = {
  clientInfo: defaultClientInfo,
  current: 0,
  fieldsEditable: false,
  isError: false,
  isLoading: false,
  isLoadingLeasing: false,
  isNewOrderError: false,
  isRequestUpdated: false,
  leasingOrder: null,
  loan: defaultLoan,
  orderApp: null,
  pageTitle: '',
  requestId: '',
  shouldUpdateRequest: false,
  tsModalValues: defaultTsModalValues
};

export const newOrderSlice = createSlice({
  name: 'newOrder',
  initialState,
  reducers: {
    setPageTitle: (state, action) => {
      state.pageTitle = action.payload;
    },
    setClientInfo: (state, action: PayloadAction<TClientInfoStore | null>) => {
      if (action.payload === null) {
        state.clientInfo = defaultClientInfo;
      } else {
        state.clientInfo = {
          ...state.clientInfo,
          ...action.payload
        };
      }
    },
    setClientInfoFullName: (
      state,
      action: PayloadAction<ClientFullNameInterface>
    ) => {
      if (state.clientInfo) {
        state.clientInfo = { ...state.clientInfo, ...action.payload };
      }
    },
    setClientPhoneVerified: (state, action: PayloadAction<boolean>) => {
      if (state.clientInfo) {
        state.clientInfo.isClientPhoneNumberVerified = action.payload;
      }
    },
    setIsNewOrderError: (state, action: PayloadAction<boolean>) => {
      state.isNewOrderError = action.payload;
    },
    setCurrent: (state, action: PayloadAction<number>) => {
      state.current = action.payload;
    },
    setLoanData: (
      state,
      action: PayloadAction<{ amount: string; auto: string }>
    ) => {
      state.loan = action.payload;
    },
    setNewOrderToInitial: (state) => {
      state.orderApp = null;
    },
    resetIsError: (state) => {
      state.isError = false;
    },
    setTsModalValues: (state, action: PayloadAction<TModalTSValues>) => {
      state.tsModalValues = action.payload;
    },
    setShouldUpdateRequest: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdateRequest = action.payload;
    },
    setShouldUpdateRequestByBiometricWs: (
      state,
      action: PayloadAction<
        | boolean
        | {
            request_uuid: string;
          }
      >
    ) => {
      if (typeof action.payload === 'boolean') {
        state.shouldUpdateRequest = action.payload;
      } else if (
        state.orderApp?.uuid === action.payload.request_uuid &&
        state.orderApp.biometricSession?.isLoading
      ) {
        state.shouldUpdateRequest = true;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNewOrder.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(createNewOrder.rejected, (state) => {
        state.isLoading = false;
        state.isNewOrderError = false;
        state.isError = true;
      })
      .addCase(createNewOrder.fulfilled, (state) => {
        state.isLoading = false;
        state.isNewOrderError = false;
        state.isError = false;
      })

      .addCase(getRequestById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRequestById.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getRequestById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orderApp = orderAppMapper(action.payload);
        state.tsModalValues = tsModalInfoMapper(action.payload);

        const clientInfo: TClientInfoStore = {
          id: state.orderApp.code || generateRandomSixDiginCode(),
          lastName: state.orderApp.personalInfo.lastName,
          firstName: state.orderApp.personalInfo.firstName,
          middleName: state.orderApp.personalInfo.middleName,
          iin: state.orderApp.personalInfo.clientIin || '',
          telNum:
            state.orderApp.personalInfo.phoneNumber ||
            state.orderApp.personalInfo.workTelNumber,
          isClientPhoneNumberVerified:
            state.orderApp.personalInfo.isClientPhoneNumberVerified
        };
        state.clientInfo = clientInfo;
      })
      .addCase(getAutoCredLeasingResponsesByRequestById.pending, (state) => {
        state.isLoadingLeasing = true;
      })
      .addCase(getAutoCredLeasingResponsesByRequestById.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(
        getAutoCredLeasingResponsesByRequestById.fulfilled,
        (state, action) => {
          state.leasingOrder = orderAppLeasingMapper(action.payload[0]);
          state.isLoadingLeasing = false;
        }
      );
  }
});

export const {
  setPageTitle,
  setClientInfo,
  setClientInfoFullName,
  setCurrent,
  setLoanData,
  setNewOrderToInitial,
  resetIsError,
  setTsModalValues,
  setShouldUpdateRequest,
  setClientPhoneVerified,
  setShouldUpdateRequestByBiometricWs
} = newOrderSlice.actions;
